'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Dialog } from '@vinted/web-ui'

import {
  visibleDurationEvent,
  ClickAdEventTarget,
  VanPlacementConfigModel,
} from '@marketplace-web/ads/ads-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'

import useVisibleTimeTracker from '../../../hooks/useVisibleTimeTracker'
import useClickAdEventTracking from '../../../hooks/useClickAdEventTracking'

import VanMultiOfferContent from '../VanMultiOfferAdvertisement/VanMultiOfferContent'

type Props = {
  isOpen: boolean
  isLoading: boolean
  offerPlacements: Array<VanPlacementConfigModel>
  shouldShowBackNav: boolean
  onClose: () => void
  onNavigateBack: () => void
  onOfferShown: (() => void) | null
}

const DigitalLabelOffer = ({
  isOpen,
  isLoading,
  offerPlacements,
  shouldShowBackNav,
  onClose,
  onNavigateBack,
  onOfferShown,
}: Props) => {
  const [currentPlacementIndex, setCurrentPlacementIndex] = useState(0)

  const isImpressionTrackingEnabled = useFeatureSwitch('web_van_offer_duration_tracking')
  const { startTimingVisible, stopAndGetVisibleSeconds } = useVisibleTimeTracker()
  const { trackClickAdEvent } = useClickAdEventTracking()
  const hasTrackedRef = useRef(false)
  const { track } = useTracking()

  const handleTrackAndClose = useCallback(() => {
    if (!isOpen || hasTrackedRef.current) return

    hasTrackedRef.current = true

    if (!isImpressionTrackingEnabled) return

    const currentPlacement = offerPlacements[currentPlacementIndex]
    if (!currentPlacement?.correlationId || !currentPlacement.id) return

    const visibleSeconds = stopAndGetVisibleSeconds()
    track(
      visibleDurationEvent({
        campaignId: currentPlacement.id,
        correlationId: currentPlacement.correlationId,
        duration: visibleSeconds,
      }),
    )
  }, [
    isOpen,
    offerPlacements,
    currentPlacementIndex,
    isImpressionTrackingEnabled,
    stopAndGetVisibleSeconds,
    track,
  ])

  const finalizeOfferView = () => {
    handleTrackAndClose()
    onOfferShown?.()
    hasTrackedRef.current = false
  }

  const handleClose = (target: ClickAdEventTarget) => {
    const placement = offerPlacements[0]

    if (placement) {
      trackClickAdEvent(placement, target)
    }

    finalizeOfferView()
    onClose()
  }

  const handleNavigateBack = (target: ClickAdEventTarget) => {
    const placement = offerPlacements[0]

    if (placement) {
      trackClickAdEvent(placement, target)
    }

    finalizeOfferView()
    onNavigateBack()
  }

  useEffect(() => {
    if (isOpen && !isLoading && offerPlacements.length === 0) {
      onClose()
    }
  }, [isOpen, isLoading, offerPlacements.length, onClose])

  useEffect(() => {
    if (!isOpen) return

    startTimingVisible()
  }, [isOpen, startTimingVisible])

  useEffect(() => {
    window.addEventListener('beforeunload', handleTrackAndClose)

    return () => {
      window.removeEventListener('beforeunload', handleTrackAndClose)
    }
  }, [handleTrackAndClose])

  if (!isOpen) return null

  if (isLoading)
    return (
      <Dialog show={isOpen} testId="digital-label-offer-modal-loader">
        <ContentLoader />
      </Dialog>
    )

  if (offerPlacements.length === 0) return null

  return (
    <div data-testid="van-multi-offer-content">
      <VanMultiOfferContent
        isOpen
        onClose={handleClose}
        onNavigateBack={shouldShowBackNav ? handleNavigateBack : undefined}
        placements={offerPlacements}
        currentPlacementIndex={currentPlacementIndex}
        onPlacementIndexChange={setCurrentPlacementIndex}
      />
    </div>
  )
}

export default DigitalLabelOffer
